import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgentAuth } from '../contexts/AgentAuthContext';
import axios from 'axios';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AgentBookingForm = () => {
  const { agent } = useAgentAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    whatsappNumber: '',
    numberOfJumpers: 1,
    locationId: agent?.locationId || '',
    bookingDate: '',
    slotId: '',
    sellingPricePerJumper: agent?.reportingPrice || 3500 // Agent's selling price
  });

  const [locations, setLocations] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (formData.locationId && formData.bookingDate) {
      fetchSlots();
    }
  }, [formData.locationId, formData.bookingDate]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/locations`);
      setLocations(response.data);
      
      // If agent has assigned location, set it and fetch details
      if (agent?.locationId) {
        const agentLocation = response.data.find(loc => loc._id === agent.locationId);
        setSelectedLocation(agentLocation);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/api/slots/available`, {
        params: {
          locationId: formData.locationId,
          startDate: formData.bookingDate
        }
      });
      // API returns { slots, slotsByDate } - we need the slots array
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setSlots([]);
    }
  };

  const handleLocationChange = (locationId) => {
    const location = locations.find(loc => loc._id === locationId);
    setSelectedLocation(location);
    setFormData({
      ...formData,
      locationId,
      slotId: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('agentToken');
      
      // Calculate pricing
      const reportingPrice = agent.reportingPrice || 0;
      const sellingPrice = formData.sellingPricePerJumper * formData.numberOfJumpers;
      const reportingTotal = reportingPrice * formData.numberOfJumpers;
      const agentEarning = sellingPrice - reportingTotal;
      
      // Create booking with agent code
      const bookingData = {
        ...formData,
        agencyCode: agent.agencyCode,
        paymentType: 'credit', // Agent bookings use credit
        reportingPrice: reportingTotal,
        sellingPrice: sellingPrice,
        agentEarning: agentEarning,
        totalAmount: reportingTotal // Amount to deduct from credit
      };

      const response = await axios.post(`${API}/api/bookings/create`, bookingData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setBookingDetails(response.data.booking);
      setSuccess(true);
    } catch (error) {
      console.error('Booking error:', error);
      setError(error.response?.data?.error || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  if (success && bookingDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 border-2 border-green-500 rounded-2xl p-8 text-center">
          <CheckCircle className="mx-auto mb-4 text-green-400" size={64} />
          <h2 className="text-2xl font-bold text-white mb-4">Booking Confirmed!</h2>
          <div className="bg-gray-900 rounded-lg p-4 mb-6 text-left">
            <p className="text-gray-400 text-sm mb-2">Booking ID</p>
            <p className="text-white font-mono text-lg font-bold mb-4">{bookingDetails.bookingId}</p>
            
            <p className="text-gray-400 text-sm mb-1">Customer</p>
            <p className="text-white mb-3">{bookingDetails.customerName}</p>
            
            <p className="text-gray-400 text-sm mb-1">Date & Time</p>
            <p className="text-white mb-3">
              {new Date(bookingDetails.bookingDate).toLocaleDateString()} at {bookingDetails.slotTime}
            </p>
            
            <p className="text-gray-400 text-sm mb-1">Jumpers</p>
            <p className="text-white mb-3">{bookingDetails.numberOfJumpers}</p>
            
            <p className="text-gray-400 text-sm mb-1">Total Amount</p>
            <p className="text-white font-bold text-xl">₹{bookingDetails.totalAmount?.toLocaleString()}</p>
            
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-yellow-400 text-sm">✓ Paid via Agent Credit</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => navigate('/agent/dashboard')}
              className="w-full px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => {
                setSuccess(false);
                setBookingDetails(null);
                setFormData({
                  customerName: '',
                  email: '',
                  whatsappNumber: '',
                  numberOfJumpers: 1,
                  locationId: agent?.locationId || '',
                  bookingDate: '',
                  slotId: ''
                });
              }}
              className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
            >
              Create Another Booking
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/agent/dashboard')}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="text-white" size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Create Booking</h1>
              <p className="text-gray-400 text-sm">Book for your customer using agent credit</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-red-400 font-medium">Error</p>
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Agent Info */}
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
              <p className="text-yellow-400 font-medium mb-2">Booking as Agent</p>
              <div className={`grid ${agent?.tier === 'tier4' ? 'grid-cols-2' : 'grid-cols-3'} gap-4 text-sm`}>
                <div>
                  <p className="text-gray-400">Agency Code</p>
                  <p className="text-white font-mono">{agent?.agencyCode}</p>
                </div>
                <div>
                  <p className="text-gray-400">Reporting Price</p>
                  <p className="text-white font-bold">₹{agent?.reportingPrice?.toLocaleString() || 0}/jumper</p>
                </div>
                {agent?.tier !== 'tier4' && (
                  <div>
                    <p className="text-gray-400">Available Credit</p>
                    <p className="text-white font-bold">₹{((agent?.creditLimit || 0) - (agent?.creditUsed || 0)).toLocaleString()}</p>
                  </div>
                )}
              </div>
              {agent?.tier === 'tier4' && (
                <p className="text-gray-400 text-xs mt-3">
                  ⚠️ Tier 4: Direct payment mode - No credit facility
                </p>
              )}
            </div>

            {/* Customer Details */}
            <div>
              <h3 className="text-white font-bold mb-4 text-lg">Customer Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white font-medium mb-2">Customer Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      required
                      value={formData.customerName}
                      onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      placeholder="Full name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">WhatsApp Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="tel"
                      required
                      value={formData.whatsappNumber}
                      onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Number of Jumpers *</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.numberOfJumpers}
                      onChange={(e) => setFormData({ ...formData, numberOfJumpers: parseInt(e.target.value) })}
                      className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div>
              <h3 className="text-white font-bold mb-4 text-lg">Booking Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-white font-medium mb-2">Location *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <select
                      required
                      value={formData.locationId}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      disabled={agent?.locationId} // Disable if agent has assigned location
                      className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 disabled:opacity-50"
                    >
                      <option value="">Select location</option>
                      {locations.map(loc => (
                        <option key={loc._id} value={loc._id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  {agent?.locationId && (
                    <p className="text-yellow-400 text-xs mt-2">Your assigned location is pre-selected</p>
                  )}
                </div>

                {selectedLocation && (
                  <div className="bg-gray-900 border border-gray-600 rounded-lg p-4">
                    <p className="text-white font-medium mb-2">{selectedLocation.name}</p>
                    <p className="text-gray-400 text-sm mb-2">{selectedLocation.address}</p>
                    <p className="text-yellow-400 font-bold">₹{selectedLocation.pricePerJumper?.toLocaleString()} per jumper</p>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white font-medium mb-2">Booking Date *</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="date"
                        required
                        min={new Date().toISOString().split('T')[0]}
                        value={formData.bookingDate}
                        onChange={(e) => setFormData({ ...formData, bookingDate: e.target.value, slotId: '' })}
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-medium mb-2">Time Slot *</label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <select
                        required
                        value={formData.slotId}
                        onChange={(e) => setFormData({ ...formData, slotId: e.target.value })}
                        disabled={!formData.bookingDate}
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 disabled:opacity-50"
                      >
                        <option value="">Select slot</option>
                        {Array.isArray(slots) && slots.map(slot => (
                          <option key={slot._id} value={slot._id}>
                            {slot.startTime} - {slot.endTime} ({slot.availableSeats} seats)
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {slots.length === 0 && formData.bookingDate && (
                  <p className="text-red-400 text-sm">No slots available for selected date</p>
                )}
              </div>
            </div>

            {/* Selling Price Input */}
            {formData.numberOfJumpers > 0 && (
              <div>
                <h3 className="text-white font-bold mb-4 text-lg">Set Your Selling Price</h3>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                  <p className="text-blue-400 text-sm mb-2">
                    <strong>Your Reporting Price:</strong> ₹{agent?.reportingPrice?.toLocaleString() || 0} per jumper
                  </p>
                  <p className="text-gray-400 text-xs">
                    This is the base price you pay. Set your selling price higher to earn profit.
                  </p>
                </div>
                <div>
                  <label className="block text-white font-medium mb-2">Your Selling Price per Jumper *</label>
                  <input
                    type="number"
                    required
                    min={agent?.reportingPrice || 0}
                    step="100"
                    value={formData.sellingPricePerJumper}
                    onChange={(e) => setFormData({ ...formData, sellingPricePerJumper: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                    placeholder={`Minimum: ₹${agent?.reportingPrice || 0}`}
                  />
                  <p className="text-gray-500 text-xs mt-2">
                    Must be at least ₹{agent?.reportingPrice?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            )}

            {/* Price Summary */}
            {formData.numberOfJumpers > 0 && formData.sellingPricePerJumper >= (agent?.reportingPrice || 0) && (
              <div className="bg-gray-900 border border-gray-600 rounded-lg p-6">
                <h4 className="text-white font-bold mb-4">Price Breakdown</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Your Selling Price per Jumper</span>
                    <span className="text-white font-bold">₹{formData.sellingPricePerJumper?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Number of Jumpers</span>
                    <span className="text-white">{formData.numberOfJumpers}</span>
                  </div>
                  <div className="border-t border-gray-700 pt-3 mt-3">
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">Total Selling Price</span>
                      <span className="text-white">₹{(formData.sellingPricePerJumper * formData.numberOfJumpers).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="text-gray-400">Your Cost (Reporting Price)</span>
                      <span className="text-red-400">- ₹{((agent?.reportingPrice || 0) * formData.numberOfJumpers).toLocaleString()}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-white font-bold text-lg">Your Earning</span>
                        <span className="text-green-400 font-bold text-lg">
                          ₹{((formData.sellingPricePerJumper * formData.numberOfJumpers) - ((agent?.reportingPrice || 0) * formData.numberOfJumpers)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-3 mt-4">
                    <p className="text-yellow-400 text-sm">
                      {agent?.tier === 'tier4' 
                        ? '⚠️ Direct Payment: Customer will pay selling price directly'
                        : '✓ Reporting price will be deducted from your credit'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || slots.length === 0}
              className="w-full py-4 bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Booking...' : 'Confirm Booking'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AgentBookingForm;
